import React, { useState, useEffect, useRef } from "react";
import usePrevious from "hooks/usePrevious.hook";
export default function TimeInputs(props) {
  const {
    itemTime,
    setItemTime,
    hasFocus,
    setHasFocus,
    timeTextClass,
    handleTimeFocus,
    hourRef,
  } = props;

  const [timeHour, setTimeHour] = useState(
    (itemTime && itemTime.slice(0, itemTime.length - 4)) || 12
  );
  const [timeMinute, setTimeMinute] = useState(
    (itemTime && itemTime.slice(itemTime.length - 3, itemTime.length - 1)) ||
      "00"
  );
  const [timeMeridiem, setTimeMeridiem] = useState(
    (itemTime && itemTime.slice(itemTime.length - 1, itemTime.length)) || "p"
  );
  const hourCopyRef = useRef(null);
  const [hourWidth, setHourWidth] = useState("30px");
  const minuteRef = useRef(null);
  const [minuteWidth, setMinuteWidth] = useState("30px");
  const minuteCopyRef = useRef(null);
  const meridiemRef = useRef(null);
  const prevItemTime = usePrevious(itemTime);

  useEffect(() => {
    if (!itemTime && hasFocus) {
      setItemTime("12:00p");
    }
    if (timeHour === "") {
      setTimeHour(12);
    }
    if (timeMinute === "") {
      setTimeMinute("00");
    }
    if (timeMinute && timeMinute.length === 1) {
      setTimeMinute("0" + timeMinute);
    }
  }, [hasFocus]);

  useEffect(() => {
    hourCopyRef.current && setHourWidth(hourCopyRef.current.offsetWidth);
  }, [timeHour]);

  useEffect(() => {
    minuteCopyRef.current && setMinuteWidth(minuteCopyRef.current.offsetWidth);
  }, [timeMinute]);

  useEffect(() => {
    if (itemTime) {
      const itemTimeArr = itemTime.split(":");
      const itemTimeHour = itemTimeArr[0];
      if (timeHour !== itemTimeHour) {
        setTimeHour(itemTimeHour);
      }
      const minuteMatch = itemTimeArr[1].match(/\d+/);
      const itemTimeMinute = minuteMatch ? minuteMatch[0] : "";
      if (timeMinute !== itemTimeMinute) {
        setTimeMinute(itemTimeMinute);
      }
      const itemTimeMeridiem = itemTime.slice(
        itemTime.length - 1,
        itemTime.length
      );
      if (timeMeridiem !== itemTimeMeridiem) {
        setTimeMeridiem(itemTimeMeridiem);
      }
    }

    if (hasFocus && prevItemTime === null) {
      hourRef.current.select();
    }
  }, [itemTime]);

  useEffect(() => {
    if (itemTime !== null) {
      const newTime = timeHour.toString() + ":" + timeMinute + timeMeridiem;
      if (newTime !== itemTime) {
        setItemTime(newTime);
      }
    }
  }, [timeHour, timeMinute, timeMeridiem]);

  function handleTimeHourChange(event) {
    const value = event.target.value;
    if (value === "") {
      setTimeHour("");
      return;
    }
    const hour = parseInt(value);
    if (!isNaN(hour) && hour >= 1 && hour <= 12) {
      setTimeHour(hour);
    }
  }

  function handleTimeMinuteChange(event) {
    const value = event.target.value;
    if (value === "" || value === "00") {
      setTimeMinute(value);
      return;
    }

    const minute = parseInt(value);

    let newValue = minute.toString();
    if (value.slice(0, 1) !== "0" && value.length < 2) {
      newValue = "0" + newValue;
    }

    if (!isNaN(minute) && minute >= 0 && minute <= 59) {
      setTimeMinute(newValue);
    }
  }

  function handleTimeMeridiemChange(event) {
    const value = event.target.value;
    const letter = event.target.value
      .slice(value.length - 1, value.length)
      .toLowerCase();
    if (letter === "p" || letter === "a") {
      setTimeMeridiem(letter);
    }
  }

  function handleTimeInputsKeyDown(event) {
    if (event.key === "Enter") {
      setHasFocus && setHasFocus(false);
      hourRef.current.blur();
      minuteRef.current.blur();
      meridiemRef.current.blur();
    }
    if (event.key === "Tab" && setHasFocus) {
      setHasFocus(true);
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      incrementTimeInput(event.target.id, -1);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      incrementTimeInput(event.target.id, 1);
    }
  }

  function incrementTimeInput(id, num) {
    if (id === "hour-input") {
      incrementHour(num);
    } else if (id === "minute-input") {
      incrementMinute(num);
    } else {
      switchMeridiem();
    }
  }

  function incrementHour(num) {
    const total = num + parseInt(timeHour);
    if ((total === 12 && num === 1) || (total === 11 && num === -1)) {
      switchMeridiem();
    }
    if (total === 13) {
      setTimeHour(1);
    } else if (total === 0) {
      setTimeHour(12);
    } else {
      setTimeHour(total);
    }
  }

  function incrementMinute(num) {
    const total = num + parseInt(timeMinute);
    if (total === 60) {
      incrementHour(num);
      setTimeMinute("00");
    } else if (total < 0) {
      incrementHour(num);
      setTimeMinute(59);
    } else if (total < 10) {
      setTimeMinute("0" + total);
    } else {
      setTimeMinute(total);
    }
  }

  function switchMeridiem() {
    setTimeMeridiem((currMer) => {
      const result = currMer === "a" ? "p" : "a";
      return result;
    });
  }

  if (itemTime) {
    return (
      <>
        <span className="time-inputs" onKeyDown={handleTimeInputsKeyDown}>
          <span className="digits-outer-wrapper">
            <span className="digit-inputs">
              <input
                ref={hourRef}
                style={{ width: hourWidth }}
                className="digits time-input"
                id="hour-input"
                value={timeHour}
                onChange={handleTimeHourChange}
                onFocus={handleTimeFocus}
              />
              <span className="colon digits time-input">:</span>
              <input
                ref={minuteRef}
                className="digits time-input"
                id="minute-input"
                style={{ width: minuteWidth }}
                value={timeMinute}
                onChange={handleTimeMinuteChange}
                onFocus={handleTimeFocus}
              />
            </span>
            <div className="digit-inputs-border-bottom" />
          </span>
          <input
            ref={meridiemRef}
            className="meridiem time-input"
            id="meridiem-input"
            value={timeMeridiem}
            onChange={handleTimeMeridiemChange}
            onFocus={handleTimeFocus}
          />
        </span>

        <span className={timeTextClass}>
          <span ref={hourCopyRef} className="time-input-copy digits">
            {timeHour}
          </span>
          <span ref={minuteCopyRef} className="time-input-copy digits">
            {timeMinute}
          </span>
        </span>
      </>
    );
  } else {
    return <span onClick={handleTimeFocus}>End of Day</span>;
  }
}
